/**
 * Designer Optics — Unified Lazy Loader v2
 * ==========================================
 * Replaces: optimization.js + optz-sh.js
 *
 * KEY DESIGN: Process everything IMMEDIATELY on DOMContentLoaded.
 * Only third-party external scripts wait for interaction.
 * This matches the old optimization.js behavior that Lighthouse expects.
 *
 * v1 mistakes (fixed):
 * - v1 waited 4 seconds before processing anything — killed LCP on Lighthouse
 * - v1 deferred images/CSS until interaction — should be immediate
 * - v1 used requestIdleCallback — starved on throttled mobile CPUs
 * - v1 scanned images once — old script re-scanned on every scroll
 *
 * Version: 2.0.0
 * Date: 2026-03-25
 */

(function () {
    'use strict';

    // ── Skip optimization on cart/checkout/preview ──
    function shouldSkipOptimization() {
        var href = window.location.href;
        return (
            href.indexOf('/cart') > -1 ||
            href.indexOf('/checkout') > -1 ||
            href.indexOf('.myshopify.com') > -1
        );
    }

    // ══════════════════════════════════════════════════════
    // PHASE 1: Runs IMMEDIATELY on DOMContentLoaded
    // Everything the page needs to render — no waiting.
    // ══════════════════════════════════════════════════════

    var scriptProcessingStarted = false;
    var windowWidth = 0;

    function initImmediate() {
        windowWidth = screen.width;

        // 1. Load lazy stylesheets from _lazyStylesSheets array
        loadLazyStylesheets();

        // 2. Process data-href CSS links
        loadDataHrefCss();

        // 3. Convert lazyload2 style tags
        processLazyStyles();

        // 4. Load images, backgrounds, iframes
        scanAndLoadImages();
        lazyLoadBackgrounds();
        lazyLoadIframes();

        // 5. Process lazyload2 scripts — these contain GTM, custom JS, etc.
        if (!scriptProcessingStarted) {
            scriptProcessingStarted = true;
            processLazyScripts();
        }

        // 6. Process data-src scripts and data-href links at parse time
        processDataSrcElements();
    }

    // ── 1. Load CSS from global _lazyStylesSheets array ──
    function loadLazyStylesheets() {
        if (typeof window._lazyStylesSheets === 'undefined' || !window._lazyStylesSheets.length) return;
        for (var i = 0; i < window._lazyStylesSheets.length; i++) {
            var url = window._lazyStylesSheets[i];
            if (url && url.length > 0) {
                var link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = url;
                link.media = 'all';
                document.head.appendChild(link);
            }
        }
    }

    // ── 2. Process link[data-href] → real stylesheet ──
    function loadDataHrefCss() {
        var links = document.querySelectorAll('link[data-href]');
        for (var i = 0; i < links.length; i++) {
            var newLink = document.createElement('link');
            newLink.rel = 'stylesheet';
            newLink.href = links[i].getAttribute('data-href');
            links[i].parentNode.insertBefore(newLink, links[i]);
            links[i].parentNode.removeChild(links[i]);
        }
    }

    // ── 3. Convert style[type=lazyload2] → real style tags ──
    function processLazyStyles() {
        var styles = document.querySelectorAll('style[type="lazyload2"]');
        for (var i = 0; i < styles.length; i++) {
            var newStyle = document.createElement('style');
            newStyle.innerHTML = styles[i].innerHTML;
            styles[i].parentNode.insertBefore(newStyle, styles[i]);
            styles[i].parentNode.removeChild(styles[i]);
        }
    }

    // ── 4. Scan and load images ──
    // Called on DOMContentLoaded AND on every scroll (like old optimization.js)
    // This handles dynamically added images (e.g. search and filter results)
    function scanAndLoadImages() {
        // img.lazy2 — viewport-proximity check (matches old optimization.js)
        var images = document.querySelectorAll('img.lazy2');
        var viewportHeight = window.innerHeight;
        var lazyLoadBuffer = 200; // pixels before viewport

        for (var i = 0; i < images.length; i++) {
            var rect = images[i].getBoundingClientRect();
            // Skip images at top:0 (not yet laid out) and images too far below viewport
            if (rect.top !== 0 && rect.top - viewportHeight < lazyLoadBuffer) {
                var src = windowWidth < 600
                    ? (images[i].dataset.mobsrc || images[i].dataset.src)
                    : (images[i].dataset.src || images[i].dataset.src);
                var srcset = images[i].getAttribute('data-srcset');

                if (src && src !== '') images[i].src = src;
                if (srcset && srcset !== '') images[i].srcset = srcset;
                images[i].classList.remove('lazy2');
            }
        }

        // img[data-img-src] — IntersectionObserver (better than old approach)
        // Only set up once, observer persists and handles dynamic additions
        if (!scanAndLoadImages._imgObserverSet) {
            scanAndLoadImages._imgObserverSet = true;
            var dataImgSrcImages = document.querySelectorAll('img[data-img-src]');
            if (dataImgSrcImages.length > 0 && 'IntersectionObserver' in window) {
                var observer = new IntersectionObserver(function (entries) {
                    for (var j = 0; j < entries.length; j++) {
                        if (entries[j].isIntersecting) {
                            var img = entries[j].target;
                            var imgSrc = img.getAttribute('data-img-src') || img.getAttribute('data-src');
                            var imgSrcset = img.getAttribute('data-srcset');
                            if (imgSrc) img.src = imgSrc;
                            if (imgSrcset) img.srcset = imgSrcset;
                            observer.unobserve(img);
                        }
                    }
                }, { rootMargin: '200px 0px', threshold: 0.01 });

                for (var k = 0; k < dataImgSrcImages.length; k++) {
                    observer.observe(dataImgSrcImages[k]);
                }
            }
        }
    }

    // ── 5. Process lazyload2 scripts sequentially ──
    function processLazyScripts() {
        var scripts = document.querySelectorAll('script[type="lazyload2"]');
        if (scripts.length < 1) return;

        var first = scripts[0];
        var dataSrc = first.getAttribute('data-src');

        if (dataSrc !== null) {
            // External script with data-src — create new script, load, then continue
            var newScript = document.createElement('script');
            // Copy attributes except type and data-src
            var attrs = first.attributes;
            for (var i = 0; i < attrs.length; i++) {
                if (attrs[i].name !== 'type' && attrs[i].name !== 'data-src') {
                    newScript.setAttribute(attrs[i].name, attrs[i].value);
                }
            }
            newScript.src = dataSrc;
            newScript.onload = function () { processLazyScripts(); };
            newScript.onerror = function () { processLazyScripts(); };
            first.parentNode.insertBefore(newScript, first.nextSibling);
            first.parentNode.removeChild(first);
        } else {
            // Inline script — create new script with the content
            var inlineScript = document.createElement('script');
            var attrs2 = first.attributes;
            for (var j = 0; j < attrs2.length; j++) {
                if (attrs2[j].name !== 'type') {
                    inlineScript.setAttribute(attrs2[j].name, attrs2[j].value);
                }
            }
            inlineScript.textContent = first.textContent;
            first.parentNode.insertBefore(inlineScript, first.nextSibling);
            first.parentNode.removeChild(first);
            // Continue immediately for inline scripts
            processLazyScripts();
        }
    }

    // ── 6. Process data-src on scripts and data-href on links (parse-time) ──
    function processDataSrcElements() {
        var scripts = document.getElementsByTagName('script');
        for (var i = 0; i < scripts.length; i++) {
            if (scripts[i].getAttribute('data-src') !== null) {
                scripts[i].setAttribute('src', scripts[i].getAttribute('data-src'));
                scripts[i].removeAttribute('data-src');
            }
        }
        var links = document.getElementsByTagName('link');
        for (var j = 0; j < links.length; j++) {
            if (links[j].getAttribute('data-href') !== null) {
                links[j].setAttribute('href', links[j].getAttribute('data-href'));
                links[j].removeAttribute('data-href');
            }
        }
    }

    // ── 7. Lazy-load background images (.lazybg) ──
    function lazyLoadBackgrounds() {
        var elements = document.querySelectorAll('.lazybg');
        elements.forEach(function (el) {
            var style = windowWidth < 600
                ? (el.dataset.mobstyle || el.dataset.style)
                : (el.dataset.style || el.dataset.style);
            if (style && style !== '') {
                el.style.cssText = style;
            }
            el.classList.remove('lazybg');
        });
    }

    // ── 8. Lazy-load iframes with data-src ──
    function lazyLoadIframes() {
        var iframes = document.querySelectorAll('iframe');
        iframes.forEach(function (iframe) {
            if (iframe.dataset.src && iframe.dataset.src !== '') {
                iframe.src = iframe.dataset.src;
                delete iframe.dataset.src;
            }
        });
    }

    // ══════════════════════════════════════════════════════
    // PHASE 2: Runs on first user INTERACTION only
    // Third-party scripts that aren't needed for initial render.
    // ══════════════════════════════════════════════════════

    var thirdPartyLoaded = false;

    function loadThirdPartyOnInteraction() {
        if (thirdPartyLoaded) return;
        thirdPartyLoaded = true;

        // Remove interaction listeners
        var events = ['scroll', 'mousemove', 'touchstart', 'click', 'keydown'];
        for (var i = 0; i < events.length; i++) {
            window.removeEventListener(events[i], loadThirdPartyOnInteraction);
        }

        if (shouldSkipOptimization()) return;

        // Process data-lazy-src scripts
        var lazySrcScripts = document.querySelectorAll('script[data-lazy-src]');
        if (lazySrcScripts.length > 0) {
            var fragment = document.createDocumentFragment();
            for (var j = 0; j < lazySrcScripts.length; j++) {
                var src = lazySrcScripts[j].getAttribute('data-lazy-src');
                if (src) {
                    var s = document.createElement('script');
                    s.src = src;
                    s.defer = true;
                    fragment.appendChild(s);
                    lazySrcScripts[j].parentNode.removeChild(lazySrcScripts[j]);
                }
            }
            document.body.appendChild(fragment);
        }

        // Load third-party scripts
        // NOTE: our wishlist (assets/do-wishlist.js) is loaded deferred from snippets/do-wishlist.liquid, not from here.
        var deferredScripts = [
            'https://d275fvz7g8rvo.cloudfront.net/designeroptics-com.myshopify.com/online_store_script.js?shop=designeroptics-com.myshopify.com'
        ];

        for (var k = 0; k < deferredScripts.length; k++) {
            var script = document.createElement('script');
            script.src = deferredScripts[k];
            script.async = true;
            document.body.appendChild(script);
        }
    }

    // ══════════════════════════════════════════════════════
    // PHASE 3: Continuous — re-scan images on scroll
    // Handles dynamically added images (search results, carousels, etc.)
    // ══════════════════════════════════════════════════════

    function onScrollRescan() {
        scanAndLoadImages();
    }

    // ══════════════════════════════════════════════════════
    // BOOT: Wire everything up
    // ══════════════════════════════════════════════════════

    function boot() {
        // Phase 1: IMMEDIATE — process everything the page needs
        // Match old optimization.js: skip if Linux x86_64 (server-side bots)
        if (navigator.platform !== 'Linux x86_64') {
            initImmediate();
        }

        windowWidth = screen.width;

        // Continuous scroll re-scan (like old optimization.js wnw_init on scroll)
        window.addEventListener('scroll', onScrollRescan, { passive: true });
        window.addEventListener('mousemove', function () { scanAndLoadImages(); }, { passive: true });
        window.addEventListener('touchstart', function () { scanAndLoadImages(); }, { passive: true });

        // Phase 2: Third-party scripts wait for interaction
        var interactionEvents = ['scroll', 'mousemove', 'touchstart', 'click', 'keydown'];
        for (var i = 0; i < interactionEvents.length; i++) {
            window.addEventListener(interactionEvents[i], loadThirdPartyOnInteraction, { passive: true });
        }

        // On cart/checkout/preview: load third-party immediately too
        if (shouldSkipOptimization()) {
            loadThirdPartyOnInteraction();
        }
    }

    // Start on DOMContentLoaded (same as old optimization.js)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

})();
